
import { Component, Vue, Watch } from "vue-property-decorator";
import Moment from "moment";
import httpClient from "@/store/httpClient";
import rules from "@/store/rules";
import { CatalogueSortingType } from "@/dtos/catalogueSortingType";
import DatePicker from "@/views/Settings/DatePicker.vue";
import { AddressDto } from "@/dtos/addressDto";
import { CartItem } from "@/dtos/cartItem";
import { CustomerArticleDto } from "@/dtos/customerArticleDto";
import { OfferRequestDto } from "@/dtos/offerRequestDto";
import { OfferRequestPositionDto } from "@/dtos/offerRequestPositionDto";
import { OfferRequestOrderType } from "@/dtos/offerRequestOrderType";
import { OfferRequestDeliveryType } from "@/dtos/offerRequestDeliveryType";

@Component({
	components: {
		DatePicker,
	},
})
export default class CreateOfferRequest extends Vue {
	private request: OfferRequestDto = new OfferRequestDto();
	private positionsExpanded: boolean = true;
	private articlePickerOpen: boolean = false;
	private articleSearchTerm: string = "";
	private pickerArticles: CustomerArticleDto[] = new Array<CustomerArticleDto>();
	private pickerBatchSize: number = 50;
	private isLoadingArticles: boolean = false;
	private searchDebounceTimer: number | null = null;

	private quantityRules = rules.allowedCartAmountRule;
	private callOffRules = [
		(v: number) => !!v || (this.$t("store.rules.required") as string),
		(v: number) => !isNaN(v) || (this.$t("store.rules.notANumber") as string),
		(v: number) => v % 1 === 0 || (this.$t("store.rules.noDecimals") as string),
		(v: number) => v >= 1 || (this.$t("store.rules.mustBePositive") as string),
	];

	private mounted() {
		this.copyPositionsFromCart();
		this.presetDeliveryAddress();
	}

	// ---------------------------------------------------------------- enums
	// Exposed so the template can name the values instead of using raw numbers.
	private get orderTypes() {
		return OfferRequestOrderType;
	}
	private get deliveryTypes() {
		return OfferRequestDeliveryType;
	}

	// ------------------------------------------------------------ positions
	private get positions(): OfferRequestPositionDto[] {
		return this.request.positions;
	}

	private get totalQuantity(): number {
		return this.positions.reduce(
			(sum: number, position: OfferRequestPositionDto) => sum + (position.quantity || 0),
			0
		);
	}

	/**
	 * Takes over the articles and quantities of the cart once. From here on the
	 * request owns its positions, so later edits never touch the cart.
	 */
	private copyPositionsFromCart() {
		const cart = this.$store.state.currentCart;
		if (cart === null || cart === undefined) {
			return;
		}
		this.request.positions = cart.cartItems.map((item: CartItem) => {
			const position = new OfferRequestPositionDto();
			position.articleId = item.articleId;
			position.quantity = item.quantity;
			position.inspectionQuantity = 1;
			return position;
		});
	}

	private removePosition(position: OfferRequestPositionDto) {
		const index = this.positions.indexOf(position);
		if (index >= 0) {
			this.positions.splice(index, 1);
		}
	}

	private addPosition(article: CustomerArticleDto) {
		const position = new OfferRequestPositionDto();
		position.articleId = article.articleId;
		position.quantity = 1;
		position.inspectionQuantity = 1;
		// A new position starts unselected, even while the inspection columns are shown.
		position.isSelectedForInspection = false;
		this.positions.push(position);
	}

	// -------------------------------------------------------------- articles
	private get allArticles(): CustomerArticleDto[] {
		return this.$store.state.articles as CustomerArticleDto[];
	}

	private article(articleId: number): CustomerArticleDto | undefined {
		return this.allArticles.find((a: CustomerArticleDto) => a.articleId === articleId);
	}

	private articleName(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return this.$t("offerRequest.form.positions.articleNumber", {
				id: articleId,
			}).toString();
		}
		return article.customerArticleName || article.customerArticleId;
	}

	private articleMeta(articleId: number): string {
		const article = this.article(articleId);
		if (article === undefined) {
			return "";
		}
		const number = this.$t("offerRequest.form.positions.articleNumber", { id: articleId });
		if (article.isAssembly) {
			return number.toString();
		}
		return `${number} · ${article.materialname}`;
	}

	// The article's lastChange is passed on purpose: without it the getter stamps the
	// current time into the url, so every re-render would request the image again.
	private thumbnailUrl(articleId: number): string {
		const article = this.article(articleId);
		return this.$store.getters.getThumbnailUrl(
			articleId,
			article ? article.lastChange : undefined
		);
	}

	/** Orderable articles from the last search that are not part of the request yet. */
	private get selectableArticles(): CustomerArticleDto[] {
		return this.pickerArticles.filter((article: CustomerArticleDto) => {
			if (!article.canBeProduced || article.calculating || article.parentArticleId > 0) {
				return false;
			}
			return !this.positions.some(
				(p: OfferRequestPositionDto) => p.articleId === article.articleId
			);
		});
	}

	@Watch("articlePickerOpen")
	private onArticlePickerToggled(isOpen: boolean) {
		if (isOpen) {
			this.articleSearchTerm = "";
			this.loadSelectableArticles();
		}
	}

	@Watch("articleSearchTerm")
	private onArticleSearchTermChanged() {
		if (this.searchDebounceTimer !== null) {
			clearTimeout(this.searchDebounceTimer);
		}
		this.searchDebounceTimer = window.setTimeout(() => this.loadSelectableArticles(), 400);
	}

	/**
	 * Searches the customer's articles on the server. Going to the server keeps the picker
	 * complete: the store only holds the articles another view happened to load. Terms
	 * shorter than three characters are sent as an empty search, as in the catalogue.
	 */
	private async loadSelectableArticles() {
		const term = (this.articleSearchTerm || "").trim();
		const relevantTerm = term.length >= 3 ? term : "";
		this.isLoadingArticles = true;
		try {
			const response = await httpClient().get(
				`article/getArticlesOfCustomer?searchTerm=${encodeURI(relevantTerm)}` +
					`&from=0&to=${this.pickerBatchSize}` +
					`&sorting=${CatalogueSortingType.ArticleNameAscending}&userId=`
			);
			response.data.forEach((article: any) => (article.calculatingMessage = ""));
			// Also kept in the store so the name of an added position resolves right away.
			this.$store.commit("addOrUpdateArticles", response.data);
			this.pickerArticles = response.data;
		} catch (e) {
			console.error("Failed to load articles for the offer request picker.", e);
			this.pickerArticles = new Array<CustomerArticleDto>();
		} finally {
			this.isLoadingArticles = false;
		}
	}

	// -------------------------------------------------------------- order type
	private get isOneTime(): boolean {
		return this.request.orderType === OfferRequestOrderType.OneTime;
	}

	/** For a series the window is about the first delivery, so the heading differs. */
	private get completionTitle(): string {
		return this.isOneTime
			? this.$t("offerRequest.form.orderType.completionTitle").toString()
			: this.$t("offerRequest.form.orderType.completionTitleFirstDelivery").toString();
	}

	/** Earliest date we let the customer pick, matching the usual production lead time. */
	private get earliestCompletionDate(): string {
		return Moment().add(21, "day").format("YYYY-MM-DD");
	}

	/** The end of the window can never be before its start. */
	private get completionToMinDate(): string {
		return this.request.completionFrom || this.earliestCompletionDate;
	}

	// ---------------------------------------------------------------- delivery
	private get isDelivery(): boolean {
		return this.request.deliveryType === OfferRequestDeliveryType.Delivery;
	}

	private get deliveryAddresses(): AddressDto[] {
		const customer = this.$store.state.customer;
		if (customer === null || customer === undefined) {
			return new Array<AddressDto>();
		}
		return (customer.deliveryAddresses as AddressDto[]) || new Array<AddressDto>();
	}

	private get deliveryAddressItems() {
		return this.deliveryAddresses.map((address: AddressDto) => ({
			value: address.id,
			text: `${address.companyName || address.surname}, ${address.street}, ${address.zip} ${
				address.town
			}`,
		}));
	}

	private presetDeliveryAddress() {
		const preferred = this.$store.getters.userPreferredDeliveryAddress as AddressDto;
		if (preferred && preferred.id > 0) {
			this.request.deliveryAddressId = preferred.id;
			return;
		}
		if (this.deliveryAddresses.length > 0) {
			this.request.deliveryAddressId = this.deliveryAddresses[0].id;
		}
	}

	/** Collection carries no address and no note, so both are dropped when chosen. */
	private onDeliveryTypeChanged() {
		if (!this.isDelivery) {
			this.request.deliveryAddressId = 0;
			this.request.deliveryNote = "";
		} else if (this.request.deliveryAddressId <= 0) {
			this.presetDeliveryAddress();
		}
	}

	// -------------------------------------------------------------- inspection
	/** Drives the two extra columns of the position list. */
	private get wantsInspection(): boolean {
		return this.request.wantsFirstArticleInspection;
	}

	private onInspectionWantedChanged() {
		// Nothing is preselected: the customer picks the positions in the list itself.
		this.clearInspectionSelection();
	}

	private get allSelectedForInspection(): boolean {
		return (
			this.positions.length > 0 &&
			this.positions.every((p: OfferRequestPositionDto) => p.isSelectedForInspection)
		);
	}

	private toggleSelectAll(selectAll: boolean) {
		this.positions.forEach((position: OfferRequestPositionDto) => {
			position.isSelectedForInspection = selectAll === true;
			if (!position.isSelectedForInspection) {
				position.inspectionQuantity = 1;
			}
		});
	}

	private clearInspectionSelection() {
		this.positions.forEach((position: OfferRequestPositionDto) => {
			position.isSelectedForInspection = false;
			position.inspectionQuantity = 1;
		});
	}

	private get selectedInspectionPositions(): OfferRequestPositionDto[] {
		return this.positions.filter((p: OfferRequestPositionDto) => p.isSelectedForInspection);
	}

	/** A sample can never be larger than the quantity the offer is requested for. */
	private inspectionQuantityRules(position: OfferRequestPositionDto) {
		return [
			(v: number) => !!v || (this.$t("store.rules.required") as string),
			(v: number) => !isNaN(v) || (this.$t("store.rules.notANumber") as string),
			(v: number) => v % 1 === 0 || (this.$t("store.rules.noDecimals") as string),
			(v: number) => v >= 1 || (this.$t("store.rules.mustBePositive") as string),
			(v: number) =>
				v <= (position.quantity || 0) ||
				(this.$t("offerRequest.form.inspection.quantityTooHigh", {
					max: position.quantity,
				}) as string),
		];
	}

	// -------------------------------------------------------------- validation
	/** Named reasons why the request cannot be sent yet. Empty means ready to send. */
	private get blockingReasons(): string[] {
		const reasons = new Array<string>();
		if (this.positions.length === 0) {
			reasons.push(this.$t("offerRequest.form.blockers.positions").toString());
		}
		if (!this.request.completionFrom || !this.request.completionTo) {
			reasons.push(this.$t("offerRequest.form.blockers.completionWindow").toString());
		}
		if (
			this.request.completionFrom &&
			this.request.completionTo &&
			this.request.completionTo < this.request.completionFrom
		) {
			reasons.push(this.$t("offerRequest.form.blockers.completionOrder").toString());
		}
		if (
			!this.isOneTime &&
			(!this.request.callOffsPerYear || this.request.callOffsPerYear < 1)
		) {
			reasons.push(this.$t("offerRequest.form.blockers.callOffs").toString());
		}
		if (
			this.isDelivery &&
			this.deliveryAddresses.length > 0 &&
			this.request.deliveryAddressId <= 0
		) {
			reasons.push(this.$t("offerRequest.form.blockers.address").toString());
		}
		if (this.wantsInspection && this.selectedInspectionPositions.length === 0) {
			reasons.push(this.$t("offerRequest.form.blockers.inspectionPositions").toString());
		}
		if (
			this.wantsInspection &&
			this.selectedInspectionPositions.some(
				(p: OfferRequestPositionDto) =>
					!p.inspectionQuantity ||
					p.inspectionQuantity < 1 ||
					p.inspectionQuantity % 1 !== 0
			)
		) {
			reasons.push(this.$t("offerRequest.form.blockers.inspectionQuantity").toString());
		}
		return reasons;
	}

	// ------------------------------------------------------------------ submit
	/**
	 * Assembles the request. There is no backend endpoint yet, so the prototype
	 * logs the payload and tells the customer that nothing was transmitted.
	 */
	private submit() {
		if (this.blockingReasons.length > 0) {
			return;
		}
		if (this.request.deliveryType === OfferRequestDeliveryType.Pickup) {
			this.request.deliveryAddressId = 0;
			this.request.deliveryNote = "";
		}
		// The completion window applies to both order types, the call-off count does not.
		if (this.isOneTime) {
			this.request.callOffsPerYear = null;
		}
		console.log("OfferRequest (not yet sent):", JSON.parse(JSON.stringify(this.request)));
		this.$store.dispatch("setSnackbarText", this.$t("offerRequest.form.prototypeNotice"));
		this.$router.push({ name: "OfferRequests" });
	}
}
